<template>
  <a-spin :spinning="loading">
    <a-alert
      message="节点到期提醒已启用后，系统每分钟检查一次节点有效期；紧急和已过期提醒会立即发邮件，其余提醒按每日汇总发送。"
      type="info"
      show-icon
      class="settings-alert"
    />
    <a-form layout="vertical" @finish="onSave">
      <section class="settings-section">
        <div class="section-heading"><div><h3>站内提醒</h3><p>提醒会显示在顶部铃铛和通知中心内。</p></div></div>
        <div class="switch-row"><div><strong>节点到期提醒</strong><small>根据节点的有效期生成提醒</small></div><a-switch v-model:checked="form.rules.node_enabled" /></div>
        <div class="switch-row"><div><strong>节点到期提醒</strong><small>同时保留节点有效期提醒能力</small></div><a-switch v-model:checked="form.rules.enabled" /></div>
        <div class="threshold-grid">
          <a-form-item label="提前提醒（天）"><a-input-number v-model:value="form.rules.upcoming_days" :min="4" :max="365" /></a-form-item>
          <a-form-item label="临近（天）"><a-input-number v-model:value="form.rules.soon_days" :min="2" :max="364" /></a-form-item>
          <a-form-item label="紧急（天）"><a-input-number v-model:value="form.rules.urgent_days" :min="1" :max="363" /></a-form-item>
        </div>
        <p class="form-tip">需满足：提前提醒天数 &gt; 临近天数 &gt; 紧急天数。</p>
      </section>

      <section class="settings-section">
        <div class="section-heading"><div><h3>邮件通知</h3><p>SMTP 密码加密保存，页面和接口都不会回显。</p></div><a-switch v-model:checked="form.smtp.enabled" /></div>
        <template v-if="form.smtp.enabled">
          <div class="smtp-grid">
            <a-form-item label="SMTP 服务器" required><a-input v-model:value="form.smtp.host" placeholder="smtp.example.com" /></a-form-item>
            <a-form-item label="端口" required><a-input-number v-model:value="form.smtp.port" :min="1" :max="65535" style="width: 100%" /></a-form-item>
            <a-form-item label="加密方式"><a-select v-model:value="form.smtp.security"><a-select-option value="ssl">SSL/TLS</a-select-option><a-select-option value="starttls">STARTTLS</a-select-option><a-select-option value="none">不加密</a-select-option></a-select></a-form-item>
            <a-form-item label="SMTP 用户名"><a-input v-model:value="form.smtp.username" autocomplete="username" /></a-form-item>
            <a-form-item label="SMTP 密码" :extra="passwordExtra"><a-input-password v-model:value="smtpPassword" autocomplete="new-password" placeholder="留空则保留现有密码" /></a-form-item>
            <a-form-item label="发件人名称"><a-input v-model:value="form.smtp.from_name" placeholder="节点通知中心" /></a-form-item>
            <a-form-item label="发件邮箱" required><a-input v-model:value="form.smtp.from_address" placeholder="ops@example.com" /></a-form-item>
            <a-form-item label="收件邮箱" required class="recipients-item"><a-textarea v-model:value="recipientsText" :rows="3" placeholder="每行一个邮箱，或使用逗号分隔" /></a-form-item>
          </div>
          <div class="digest-row">
            <div><strong>每日汇总邮件</strong><small>发送所有待处理的到期提醒</small></div>
            <a-switch v-model:checked="form.rules.digest_enabled" />
            <a-time-picker v-model:value="digestTime" format="HH:mm" value-format="HH:mm" :disabled="!form.rules.digest_enabled" />
          </div>
        </template>
      </section>

      <a-space wrap>
        <a-button type="primary" html-type="submit" :loading="saving">保存通知设置</a-button>
        <a-button v-if="form.smtp.enabled" :loading="testing" @click="onTest">发送测试邮件</a-button>
        <a-popconfirm v-if="form.smtp.password_configured" title="确认清除已保存的 SMTP 密码？" @confirm="onClearPassword">
          <a-button danger>清除已保存密码</a-button>
        </a-popconfirm>
      </a-space>
    </a-form>
  </a-spin>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { message } from 'ant-design-vue';
import {
  clearSMTPPassword,
  getNotificationSettings,
  saveNotificationSettings,
  testNotificationEmail,
  type NotificationSettings,
} from '../api/notifications';

const defaultForm = (): NotificationSettings => ({
  rules: { enabled: true, node_enabled: true, upcoming_days: 30, soon_days: 7, urgent_days: 3, digest_enabled: true, digest_time: '09:00', timezone: 'Asia/Shanghai' },
  smtp: { enabled: false, host: 'smtp.qq.com', port: 465, security: 'ssl', username: '704482843@qq.com', from_address: '704482843@qq.com', from_name: '热血江湖节点通知', recipients: ['704482843@qq.com'], password_configured: false },
});
const form = reactive<NotificationSettings>(defaultForm());
const loading = ref(false);
const saving = ref(false);
const testing = ref(false);
const smtpPassword = ref('');
const recipientsText = ref('');
const digestTime = computed({ get: () => form.rules.digest_time, set: (value: string) => { form.rules.digest_time = value || '09:00'; } });
const passwordExtra = computed(() => form.smtp.password_configured ? '已保存密码；留空不会修改。' : '尚未保存密码。');

const syncRecipients = () => {
  form.smtp.recipients = [...new Set(recipientsText.value.split(/[\n,;]+/).map((value) => value.trim()).filter(Boolean))];
};
const load = async () => {
  loading.value = true;
  try {
    const data = await getNotificationSettings();
    Object.assign(form.rules, data.rules);
    Object.assign(form.smtp, data.smtp);
    recipientsText.value = (data.smtp.recipients || []).join('\n');
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '加载通知设置失败');
  } finally { loading.value = false; }
};
const onSave = async () => {
  syncRecipients();
  saving.value = true;
  try {
    const result = await saveNotificationSettings(form, smtpPassword.value);
    form.smtp.password_configured = result.password_configured;
    smtpPassword.value = '';
    message.success('通知设置已保存');
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '保存失败');
  } finally { saving.value = false; }
};
const onTest = async () => {
  syncRecipients();
  testing.value = true;
  try {
    const result = await testNotificationEmail(form, smtpPassword.value);
    message.success(`测试邮件已发送到 ${result.sent_count} 个收件地址`);
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '测试邮件发送失败');
  } finally { testing.value = false; }
};
const onClearPassword = async () => {
  try {
    await clearSMTPPassword();
    form.smtp.password_configured = false;
    smtpPassword.value = '';
    message.success('SMTP 密码已清除');
  } catch (e: any) { message.error(e.response?.data?.error?.message || '清除失败'); }
};
onMounted(load);
</script>

<style scoped>
.settings-alert { margin-bottom: 18px; }
.settings-section { margin-bottom: 18px; padding: 16px; border: 1px solid #e4e9f0; border-radius: 12px; background: #fafbfd; }
.section-heading, .switch-row, .digest-row { display: flex; align-items: center; justify-content: space-between; gap: 14px; }
.section-heading { margin-bottom: 14px; }
.section-heading h3 { margin: 0; color: #1d3045; font-size: 16px; }
.section-heading p, .switch-row small, .digest-row small { display: block; margin: 3px 0 0; color: #7b8797; font-size: 11px; }
.switch-row { padding: 11px 0; border-top: 1px solid #e9edf2; }
.threshold-grid, .smtp-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0 12px; margin-top: 8px; }
.threshold-grid :deep(.ant-input-number) { width: 100%; }
.form-tip { margin: -12px 0 0; color: #8793a3; font-size: 11px; }
.smtp-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.recipients-item { grid-column: 1 / -1; }
.digest-row { padding-top: 12px; border-top: 1px solid #e9edf2; }
.digest-row > div { flex: 1; }
@media (max-width: 767px) {
  .settings-section { padding: 13px; }
  .threshold-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0 7px; }
  .smtp-grid { grid-template-columns: 1fr; }
  .recipients-item { grid-column: auto; }
  .digest-row { align-items: flex-start; flex-wrap: wrap; }
  .digest-row > div { flex-basis: 100%; }
}
</style>
